// Phase 9 R2 — Edit inventory item dialog (whitelisted metadata only).
// No quantity, no active toggle, no system fields.
import { useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useUpdateInventoryItem } from '@/hooks/inventory/useUpdateInventoryItem';
import type { InventoryItem } from '@/hooks/inventory/useInventoryItems';

const schema = z.object({
  part_number: z.string().trim().min(1),
  brand: z.string().trim().optional(),
  name: z.string().trim().min(1),
  description: z.string().trim().optional(),
  category: z.string().trim().optional(),
  supplier: z.string().trim().optional(),
  supplier_part_number: z.string().trim().optional(),
  unit_cost: z.coerce.number().min(0),
  unit_sell_default: z.coerce.number().min(0).optional().or(z.literal('').transform(() => undefined)),
  reorder_point: z.coerce.number().min(0),
  reorder_quantity: z.coerce.number().min(0),
  bin_location: z.string().trim().optional(),
  notes: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

export function EditInventoryItemDialog({
  item,
  open,
  onOpenChange,
}: {
  item: InventoryItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const update = useUpdateInventoryItem();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      part_number: '',
      name: '',
      unit_cost: 0,
      reorder_point: 0,
      reorder_quantity: 0,
    },
  });

  useEffect(() => {
    if (item && open) {
      form.reset({
        part_number: item.part_number,
        brand: item.brand ?? '',
        name: item.name,
        description: item.description ?? '',
        category: item.category ?? '',
        supplier: item.supplier ?? '',
        supplier_part_number: item.supplier_part_number ?? '',
        unit_cost: Number(item.unit_cost ?? 0),
        unit_sell_default: item.unit_sell_default == null ? undefined : Number(item.unit_sell_default),
        reorder_point: Number(item.reorder_point ?? 0),
        reorder_quantity: Number(item.reorder_quantity ?? 0),
        bin_location: item.bin_location ?? '',
        notes: item.notes ?? '',
      });
    }
  }, [item, open]);

  const submit = async (values: FormValues) => {
    if (!item) return;
    await update.mutateAsync({
      id: item.id,
      part_number: values.part_number,
      brand: values.brand || null,
      name: values.name,
      description: values.description || null,
      category: values.category || null,
      supplier: values.supplier || null,
      supplier_part_number: values.supplier_part_number || null,
      unit_cost: values.unit_cost,
      unit_sell_default: values.unit_sell_default ?? null,
      reorder_point: values.reorder_point,
      reorder_quantity: values.reorder_quantity,
      bin_location: values.bin_location || null,
      notes: values.notes || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Inventory Item</DialogTitle>
          <DialogDescription>
            Quantity changes happen through Adjust or Restock — not here.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField name="part_number" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Part Number *</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField name="brand" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Brand</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
              <FormField name="name" control={form.control} render={({ field }) => (
                <FormItem className="md:col-span-2"><FormLabel>Name *</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField name="description" control={form.control} render={({ field }) => (
                <FormItem className="md:col-span-2"><FormLabel>Description</FormLabel><FormControl><Textarea rows={2} {...field} /></FormControl></FormItem>
              )} />
              <FormField name="category" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Category</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
              <FormField name="supplier" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Supplier</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
              <FormField name="supplier_part_number" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Supplier Part #</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
              <FormField name="bin_location" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Bin Location</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
              <FormField name="unit_cost" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Unit Cost *</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField name="unit_sell_default" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Default Sell</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl></FormItem>
              )} />
              <FormField name="reorder_point" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Reorder Point</FormLabel><FormControl><Input type="number" step="1" min="0" {...field} /></FormControl></FormItem>
              )} />
              <FormField name="reorder_quantity" control={form.control} render={({ field }) => (
                <FormItem><FormLabel>Reorder Quantity</FormLabel><FormControl><Input type="number" step="1" min="0" {...field} /></FormControl></FormItem>
              )} />
              <FormField name="notes" control={form.control} render={({ field }) => (
                <FormItem className="md:col-span-2"><FormLabel>Notes</FormLabel><FormControl><Textarea rows={2} {...field} /></FormControl></FormItem>
              )} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
